import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Loader2, Download, TrendingUp, TrendingDown, Users, DollarSign } from 'lucide-react';
import { useAgentPerformance } from '@/hooks/use-agent-performance';
import { exportToCSV } from '@/services/reportService';
import { Badge } from '@/components/ui/badge';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from 'recharts';

const COLORS = ['hsl(var(--primary))', 'hsl(var(--secondary))', 'hsl(var(--accent))', 'hsl(var(--muted))'];

const AgentPerformanceReport = () => {
  const [dateRange, setDateRange] = useState('last_30_days');
  const { data: performanceData, isLoading, error } = useAgentPerformance(dateRange);

  const handleExport = () => {
    if (!performanceData) return;

    const exportData = performanceData.rankings.map(agent => ({
      'Agent Name': agent.agent_name,
      'Quality Score': `${agent.quality_score.toFixed(1)}%`,
      'Total Procurements': agent.total_procurements,
      'Completed Procurements': agent.completed_procurements,
      'Completion Rate': `${agent.completion_rate.toFixed(1)}%`,
      'Rejection Rate': `${agent.rejection_rate.toFixed(1)}%`,
      'Total Value': `$${agent.total_value.toLocaleString()}`,
      'Average Delivery Time': `${agent.avg_delivery_time.toFixed(1)} days`,
      'On-Time Deliveries': agent.on_time_deliveries,
      'Late Deliveries': agent.late_deliveries,
      'Region': agent.region,
    }));

    exportToCSV(exportData, `agent-performance-${dateRange}-${new Date().toISOString().split('T')[0]}.csv`);
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex justify-center items-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  if (error || !performanceData) {
    return (
      <Card>
        <CardContent className="flex justify-center items-center py-12">
          <p className="text-muted-foreground">Failed to load agent performance data</p>
        </CardContent>
      </Card>
    );
  }

  const { summary, rankings, regionalPerformance, deliveryPerformance } = performanceData;

  return (
    <div className="space-y-6">
      {/* Header with filters */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Agent Performance Report</h2>
          <p className="text-muted-foreground">
            Performance metrics and analytics for all agents
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Select date range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="last_30_days">Last 30 Days</SelectItem>
              <SelectItem value="last_3_months">Last 3 Months</SelectItem>
              <SelectItem value="last_6_months">Last 6 Months</SelectItem>
              <SelectItem value="this_year">This Year</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={handleExport} variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Agents</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary.total_agents}</div>
            <p className="text-xs text-muted-foreground">
              Active agents in network
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Value</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${summary.total_value.toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">
              Total procurement value
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Quality Score</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {summary.average_quality_score.toFixed(1)}%
            </div>
            <p className="text-xs text-muted-foreground">
              Network average performance
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">On-Time Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {summary.average_on_time_rate.toFixed(1)}%
            </div>
            <p className="text-xs text-muted-foreground">
              Average on-time delivery
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Regional Performance Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Performance by Region</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={regionalPerformance}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="region" />
                <YAxis />
                <Tooltip 
                  formatter={(value, name) => [
                    name === 'avg_quality_score' ? `${Number(value).toFixed(1)}%` : value,
                    name === 'avg_quality_score' ? 'Avg Quality Score' : 
                    name === 'total_agents' ? 'Total Agents' : name
                  ]}
                />
                <Bar dataKey="avg_quality_score" fill="hsl(var(--primary))" name="Quality Score" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Delivery Performance Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Delivery Performance</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={deliveryPerformance}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {deliveryPerformance.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Top Performers and Rankings */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Performers */}
        <Card>
          <CardHeader>
            <CardTitle>Top Performing Agents</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {rankings.slice(0, 5).map((agent, index) => (
                <div key={agent.agent_id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold">
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-medium">{agent.agent_name}</p>
                      <p className="text-sm text-muted-foreground">{agent.region}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-lg">{agent.quality_score.toFixed(1)}%</p>
                    <p className="text-sm text-muted-foreground">
                      ${agent.total_value.toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Agents Needing Improvement */}
        <Card>
          <CardHeader>
            <CardTitle>Agents Needing Improvement</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {rankings
                .filter(agent => agent.quality_score < 70)
                .slice(0, 5)
                .map((agent) => (
                  <div key={agent.agent_id} className="flex items-center justify-between p-3 rounded-lg bg-destructive/10">
                    <div>
                      <p className="font-medium">{agent.agent_name}</p>
                      <p className="text-sm text-muted-foreground">{agent.region}</p>
                    </div>
                    <div className="text-right">
                      <Badge variant="destructive">
                        {agent.quality_score.toFixed(1)}%
                      </Badge>
                      <p className="text-sm text-muted-foreground mt-1">
                        {agent.rejection_rate.toFixed(1)}% rejection rate
                      </p>
                    </div>
                  </div>
                ))}
              {rankings.filter(agent => agent.quality_score < 70).length === 0 && (
                <div className="text-center py-6">
                  <TrendingUp className="h-12 w-12 text-green-500 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">
                    All agents are performing well!
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Rankings Table */}
      <Card>
        <CardHeader>
          <CardTitle>Complete Agent Rankings</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Rank</th>
                  <th className="text-left p-2">Agent</th>
                  <th className="text-left p-2">Region</th>
                  <th className="text-right p-2">Quality Score</th>
                  <th className="text-right p-2">Procurements</th>
                  <th className="text-right p-2">Completion Rate</th>
                  <th className="text-right p-2">Rejection Rate</th>
                  <th className="text-right p-2">Total Value</th>
                  <th className="text-right p-2">Avg Delivery</th>
                </tr>
              </thead>
              <tbody>
                {rankings.map((agent, index) => (
                  <tr key={agent.agent_id} className="border-b hover:bg-muted/50">
                    <td className="p-2">
                      <Badge variant={index < 3 ? "default" : "secondary"}>
                        #{index + 1}
                      </Badge>
                    </td>
                    <td className="p-2 font-medium">{agent.agent_name}</td>
                    <td className="p-2">{agent.region}</td>
                    <td className="p-2 text-right">
                      <Badge variant={agent.quality_score >= 80 ? "default" : 
                                   agent.quality_score >= 60 ? "secondary" : "destructive"}>
                        {agent.quality_score.toFixed(1)}%
                      </Badge>
                    </td>
                    <td className="p-2 text-right">{agent.total_procurements}</td>
                    <td className="p-2 text-right">{agent.completion_rate.toFixed(1)}%</td>
                    <td className="p-2 text-right">{agent.rejection_rate.toFixed(1)}%</td>
                    <td className="p-2 text-right">${agent.total_value.toLocaleString()}</td>
                    <td className="p-2 text-right">{agent.avg_delivery_time.toFixed(1)}d</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AgentPerformanceReport;